import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { logPageView } from '../../models/Analytics';
import { MainContainer } from '../MainContainer';
import './styles.less';

interface IProps extends RouteComponentProps<any> {
	className?: string;
}

interface IState {
	errorMessage?: string;
	requestInviteEmail?: string;
	showConfirmation?: boolean;
	showLoader?: boolean;
}

export class Home extends React.Component<IProps, IState> {
	public state: IState = {
		errorMessage: null,
		requestInviteEmail: '',
		showConfirmation: false,
		showLoader: false,
	};

	public componentDidMount() {
		window.scrollTo(0, 0);
		document.title = 'Levitate - Grow your business by staying top of mind through authentic, personal communication';
		logPageView('Home');
	}

	public render() {
		return (
			<MainContainer activeSection='home'>
				<div className='home-heroContainer'>
					<div className='home-headlineContainer'>
						<h1 className='home-headline'>
							Grow Your Business by Staying Top of Mind.
						</h1>
						<h2 className='home-subHeadline'>
							Levitate helps relationship-based businesses keep in touch with clients, prospects, and referral sources.
						</h2>
					</div>
				</div>
				<div className='home-featureContainer'>
					<h2 className='home-featureHeadline'>
						Keep in touch. Remember key details. Appear superhuman.
					</h2>
					<div className='home-featureBulletIntro'>
						Keeping in touch consistently with your network is way too much to manage in your head or on a spreadsheet.
						Levitate helps you build and maintain authentic relationships, which drive more organic referral and word of mouth business (the best kind!).
					</div>
					<div className='home-features-bulletContainer'>
						<div className='home-features-bulletGraphic' />
						<div className='home-features-bulletInnerContainer'>
							<div className='home-featureBullet'>
								Connect Levitate to your email / calendar system (we work with almost all of them).
							</div>
							<div className='home-featureBullet'>
								Import your contacts from your address book, CRM, or just about any system that can export a spreadsheet.
							</div>
							<div className='home-featureBullet'>
								Set simple rules about who you want to stay in touch with and how often.
							</div>
							<div className='home-featureBullet'>
								Levitate's AI will work in the background and send you a weekly email with a list of
								people you haven't emailed or met with.
							</div>
							<div className='home-featureBullet'>
								When it comes time to keep in touch, we make it easy to reach out in a personalized but efficient way.
								Scroll down to find out how.
							</div>
						</div>
					</div>
				</div>
				<div className='home-featureContainerWhiteBg'>
					<div className='home-featureContent'>
						<h1 className='home-features-headline'>Messaging and Templates</h1>

						<div className='home-features-paragraph'>
							For years, relationship-based businesses have been faced with the choice
							of either sending impersonal email marketing blasts that rarely get opened and do
							very little to deepen relationships, or spending hundreds of hours in Outlook writing
							one-off messages and making liberal use of copy/paste.
						</div>

						<div className='home-features-paragraph'>
							With Levitate's group messaging and template functionality, you don't have to choose
							between authenticity and efficiency. When it's time to keep in touch with a group of
							important contacts, you can start with a template and then customize it for each person
							based on key facts and details specific to them.
						</div>

						<div className='home-features-paragraph'>
							Levitate will send your messages individually through your own email system (not some email marketing server)
							and the messages will show up in your sent message folder just as if you sent them from Outlook or your mobile device.
						</div>
					</div>
					<div className='home-featureScreenshot-messaging-container'>
						<div className='home-featureScreenshot-messaging' />
					</div>
				</div>
				<div className='home-featureContainerGrayBg'>
					<div className='home-featureScreenshot-analytics-container'>
						<div className='home-features-screenshot-analytics' />
					</div>
					<div className='home-featureContentWrapReverse'>
						<h1 className='home-features-headline'>Relationship Analytics</h1>

						<div className='home-features-paragraph'>
							As the old saying goes, what doesn't get measured doesn't get done.
						</div>

						<div className='home-features-paragraph'>
							Levitate makes it easy to see how well you and your team are doing keeping in touch.
							We'll deliver a weekly report right to your inbox to tell you which groups you're keeping in touch with,
							and give you a targeted list of folks that you haven't reached out to in a while.
						</div>

						<div className='home-features-paragraph'>
							For CEOs, owners, and partners, our monthly Owner's Report gives you visibility into how each of your team members
							is doing with various groups that you want to keep in touch with, such as clients, partners, and referral sources.
						</div>

						<div className='home-features-paragraph'>
							And if you're ever curious about a particular contact, just visit their Levitate profile page and click "More Analysis"
							to see summary statistics about meetings, emails, as well as an activity feed where you can see who has been in touch with that contact and when.
						</div>
					</div>
				</div>
				<div className='home-featureContainerWhiteBg'>
					<div className='home-featureContent'>
						<h1 className='home-features-headline'>We Work Where <u>You</u> Work</h1>

						<div className='home-features-paragraph'>
							We know that you live in your email, calendar, and your smartphone.
							Levitate has deep integrations with all major email providers as well as mobile apps for iPhone and Android,
							so you don’t have to learn another software tool.
						</div>

						<div className='home-features-paragraph'>
							You can mark contacts for keep in touch, set tags, or record key facts right from Outlook, Gmail, or our mobile app.
							If someone hands you a business card at a networking event, simply snap a photo of the card using the Levitate app,
							and they can automatically be added to your keep in touch cadence.
						</div>

						<div className='home-features-paragraph'>
							Because of our tight integrations with Microsoft and Google, if you send an email to a contact or have a meeting with them,
							Levitate will automatically give you credit for that interaction without needing to specifically log it in Levitate.
						</div>

						<div className='home-downloads-container'>
							<div className='home-downloads-headline'>
								Download our email Add-Ins and mobile apps:
							</div>
							<div className='home-downloads-links'>
								<a className='home-outlookBadge' href='https://appsource.microsoft.com/en-us/product/office/WA104381521?tab=Overview' target='_blank' />
								<a className='home-gmailBadge' href='https://gsuite.google.com/marketplace/app/levitate_for_gmail/712591083975' target='_blank' />
								<a className='home-appleStoreBadge' href='https://itunes.apple.com/us/app/levitate-prm/id1369751897?ls=1&mt=8' target='_blank' />
								<a className='home-googlePlayBadge' href='https://play.google.com/store/apps/details?id=com.realmagic.levitate' target='_blank' />
							</div>
						</div>
					</div>
					<div className='home-featureScreenshot-tools-container'>
						<div className='home-features-screenshot-tools' />
					</div>
				</div>
				<div className='home-quotesContainer'>
					<span className='footer-horizontalRule' />
					<div className='home-quote'>
						<q>This is the first software I'm actually <strong>excited</strong> to use.</q>
					</div>
					<div className='home-quoteAuthor'>
						&mdash; Josh St. John, VP Strategic Partnerships &amp; Channels at Brooks Bell
					</div>
				</div>
			</MainContainer>
		);
	}
}
